import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BookOpen, X } from './icons';
import { SYNTAX_HELP_CATEGORIES } from './syntaxHelpData';
import { SyntaxCategoryList } from './SyntaxCategoryList';
import { SyntaxHelpSearch } from './SyntaxHelpSearch';
import { SyntaxContent } from './SyntaxContent';
import './SyntaxHelpPanel.css';

export function SyntaxHelpPanel({
  isOpen,
  onClose,
  defaultCategoryId = 'variables',
}) {
  const [activeCategoryId, setActiveCategoryId] = useState(defaultCategoryId);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen && defaultCategoryId) {
      setActiveCategoryId(defaultCategoryId);
    }
  }, [isOpen, defaultCategoryId]);

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Comprehensive Search across categories, items, syntax, examples, outputs, operators, notes, keywords
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];

    const results = [];

    SYNTAX_HELP_CATEGORIES.forEach((cat) => {
      const catKeywords = cat.keywords || [];
      const catNameMatch = cat.name.toLowerCase().includes(q);
      const catDescMatch = cat.description.toLowerCase().includes(q);
      const catKwMatch = catKeywords.some((kw) => kw.toLowerCase().includes(q));

      if (cat.items && cat.items.length > 0) {
        cat.items.forEach((item) => {
          const titleMatch = item.title?.toLowerCase().includes(q);
          const badgeMatch = item.badge?.toLowerCase().includes(q);
          const syntaxMatch = item.syntax?.toLowerCase().includes(q);
          const meaningMatch = item.meaning?.toLowerCase().includes(q);
          const exampleMatch = item.example?.toLowerCase().includes(q);
          const notesMatch = item.notes?.toLowerCase().includes(q);

          // Check sample output text
          let outputMatch = false;
          if (item.sampleOutput) {
            const outStr = JSON.stringify(item.sampleOutput).toLowerCase();
            outputMatch = outStr.includes(q);
          }

          // Check supported operators
          let opMatch = false;
          if (item.supportedOperators) {
            opMatch = item.supportedOperators.some(
              (op) =>
                op.operator.toLowerCase().includes(q) ||
                op.meaning.toLowerCase().includes(q) ||
                op.example.toLowerCase().includes(q)
            );
          }

          // Check common examples list
          let exMatch = false;
          if (item.commonExamples) {
            exMatch = item.commonExamples.some(
              (ex) =>
                ex.name.toLowerCase().includes(q) ||
                ex.output.toLowerCase().includes(q)
            );
          }

          if (
            catNameMatch ||
            catDescMatch ||
            catKwMatch ||
            titleMatch ||
            badgeMatch ||
            syntaxMatch ||
            meaningMatch ||
            exampleMatch ||
            notesMatch ||
            outputMatch ||
            opMatch ||
            exMatch
          ) {
            results.push({
              categoryId: cat.id,
              categoryName: cat.name,
              item,
            });
          }
        });
      }
    });

    return results;
  }, [searchQuery]);

  // Match counts by category for sidebar badges
  const itemCountsByCat = useMemo(() => {
    if (!searchQuery.trim()) return {};
    const counts = {};
    searchResults.forEach((r) => {
      counts[r.categoryId] = (counts[r.categoryId] || 0) + 1;
    });
    return counts;
  }, [searchQuery, searchResults]);

  const activeCategory = useMemo(() => {
    return (
      SYNTAX_HELP_CATEGORIES.find((cat) => cat.id === activeCategoryId) ||
      SYNTAX_HELP_CATEGORIES[0]
    );
  }, [activeCategoryId]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleCategorySelect = useCallback((catId, scrollToItemId) => {
    setActiveCategoryId(catId);
    setSearchQuery('');

    if (scrollToItemId) {
      setTimeout(() => {
        const el = document.getElementById(`syntax-${scrollToItemId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="syntax-drawer-root"
      role="dialog"
      aria-modal="true"
      aria-label="Syntax Help & Documentation"
    >
      {/* Backdrop overlay */}
      <div className="syntax-drawer-backdrop" onClick={onClose} />

      {/* Sliding Drawer Container */}
      <div className="syntax-drawer-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header matching original UI style */}
        <div className="syntax-panel-header">
          <div className="syntax-panel-header-left">
            <div className="syntax-book-icon-container">
              <BookOpen size={20} className="syntax-book-icon" />
            </div>
            <div className="syntax-panel-titles">
              <h2 className="syntax-panel-main-title">Syntax Help</h2>
              <p className="syntax-panel-sub-title">
                Interactive documentation for template syntax, variables, conditions & document formatting.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="syntax-panel-close-btn"
            onClick={onClose}
            aria-label="Close Syntax Help"
            title="Close Syntax Help (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        {/* Two-Column Body */}
        <div className="syntax-panel-body">
          {/* Left Column: Search & 9 Categories */}
          <aside className="syntax-panel-left-sidebar">
            <SyntaxHelpSearch
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />

            <SyntaxCategoryList
              categories={SYNTAX_HELP_CATEGORIES}
              activeCategoryId={activeCategoryId}
              onSelectCategory={(catId) => handleCategorySelect(catId)}
              searchQuery={searchQuery}
              itemCountsByCat={itemCountsByCat}
            />
          </aside>

          {/* Right Column: Dynamic Documentation & Sample Output Preview */}
          <main className="syntax-panel-right-content">
            <SyntaxContent
              category={activeCategory}
              searchQuery={searchQuery}
              searchResults={searchResults}
              onSelectCategory={handleCategorySelect}
              onClearSearch={handleClearSearch}
            />
          </main>
        </div>
      </div>
    </div>
  );
}
